import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Wrench, UserCheck, TrendingUp } from "lucide-react";

const tooltipStyle = {
  background: "#111827",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  fontSize: 12,
  color: "#e2e8f0",
};

function OffenderList({ title, icon: Icon, rows, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card/90 p-5 shadow-lg" data-testid={testid}>
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4 text-red-400" />
        <h3 className="font-heading text-lg font-bold uppercase tracking-tight">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No compliance issues recorded yet.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.name} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-red-500/15 font-mono text-xs font-bold text-red-300">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{r.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {r.issues} issues · {r.non_compliant} non-compliant · {r.flagged} flagged
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm font-bold text-red-300">{r.issue_rate}%</p>
                <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">of {r.total}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TrendsPanel({ trends = [], offenders = { technicians: [], advisors: [] } }) {
  const hasData = trends.some((t) => t.total > 0);
  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card/90 p-5 shadow-lg" data-testid="trend-compliance-chart">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Weekly Compliance Rate</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trends} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 11 }} unit="%" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
              <Line type="monotone" dataKey="compliance_rate" name="Compliance %" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card/90 p-5 shadow-lg" data-testid="trend-flagged-chart">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-400" />
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Flagged Mistakes / Week</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trends} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="total" name="Total Audits" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="flagged" name="Flagged" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {!hasData && (
        <p className="rounded-lg border border-border bg-secondary/30 px-4 py-3 text-center text-sm text-muted-foreground">
          Trends populate as repair orders are created across the weeks.
        </p>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <OffenderList title="Technicians — Most Non-Compliant" icon={Wrench} rows={offenders.technicians || []} testid="offenders-technicians" />
        <OffenderList title="Service Advisors — Most Non-Compliant" icon={UserCheck} rows={offenders.advisors || []} testid="offenders-advisors" />
      </div>
    </div>
  );
}
